import { defineStore } from 'pinia';
import { computed, reactive, ref, watch } from 'vue';
import { api, ApiCallError } from '@/api/client';
import type {
  EstimateOut,
  GenerationParams,
  GenerationType,
  ModelInfo,
} from '@/api/types';
import type { Template } from '@/lib/templates';
import { recordTemplateUse } from '@/lib/recent-templates';

const VIDEO_DEFAULTS: GenerationParams = {
  type: 'video',
  model_key: 'grok_imagine',
  prompt: '',
  resolution: '480p',
  duration: 10,
  aspect_ratio: '16:9',
  generate_audio: false,
};

const IMAGE_DEFAULTS: GenerationParams = {
  type: 'image',
  model_key: 'gpt_image_2',
  prompt: '',
  size: '1024x1024',
  count: 1,
};

// Templates are tuned for Seedance prompts/params. Switching to a different
// model invalidates the template fit, so the applied state clears.
const SEEDANCE_KEYS = new Set(['seedance_2', 'seedance_2_fast']);
export function isSeedanceModel(key: string): boolean {
  return SEEDANCE_KEYS.has(key);
}

export const useCreateStore = defineStore('create', () => {
  const params = reactive<GenerationParams>({ ...VIDEO_DEFAULTS });
  const models = ref<ModelInfo[]>([]);
  const cost = ref<number | null>(null);
  const canAfford = ref<boolean>(true);
  const estimating = ref(false);
  const submitError = ref<string | null>(null);
  const appliedTemplate = ref<Template | null>(null);

  function reset(type: GenerationType) {
    const base = type === 'video' ? VIDEO_DEFAULTS : IMAGE_DEFAULTS;
    Object.keys(params).forEach((k) => delete (params as any)[k]);
    Object.assign(params, base);
    appliedTemplate.value = null;
  }

  function loadFromRepeat(p: GenerationParams) {
    Object.keys(params).forEach((k) => delete (params as any)[k]);
    Object.assign(params, p);
    appliedTemplate.value = null;
  }

  function applyTemplate(t: Template): boolean {
    if (!isSeedanceModel(t.preset.model_key)) return false;
    const model = models.value.find((m) => m.key === t.preset.model_key);
    if (!model) return false;
    reset(t.kind);
    params.model_key = model.key;
    params.prompt = t.preset.prompt;
    if (t.preset.duration !== undefined && model.available_durations.includes(t.preset.duration)) {
      params.duration = t.preset.duration;
    }
    if (t.preset.resolution !== undefined && model.resolutions.includes(t.preset.resolution)) {
      params.resolution = t.preset.resolution as any;
    }
    if (
      t.preset.aspect_ratio !== undefined &&
      model.available_aspect_ratios.includes(t.preset.aspect_ratio)
    ) {
      params.aspect_ratio = t.preset.aspect_ratio as any;
    }
    if (t.preset.generate_audio !== undefined && model.supports.includes('audio')) {
      params.generate_audio = t.preset.generate_audio;
    }
    appliedTemplate.value = t;
    recordTemplateUse(t.id);
    void estimate();
    return true;
  }

  async function loadModels(type: GenerationType) {
    try {
      models.value = await api.get<ModelInfo[]>(`/models/${type}`);
    } catch {
      models.value = [];
    }
  }

  const activeModel = computed(() =>
    models.value.find((m) => m.key === params.model_key) ?? null,
  );

  function validate(): string | null {
    const m = activeModel.value;
    const promptRequired = m?.supports_prompt !== false;
    if (promptRequired && (!params.prompt || !params.prompt.trim())) {
      return 'Опишите, что должно происходить';
    }
    if (params.type === 'video') {
      const hasRefs =
        (params.reference_image_urls?.length ?? 0) > 0 ||
        (params.reference_video_urls?.length ?? 0) > 0 ||
        (params.reference_audio_urls?.length ?? 0) > 0;
      const hasFirst = !!params.first_frame_url;
      const hasEnd = !!params.end_frame_url;
      // Most models treat refs and frames as alternatives (kie rejects the
      // combo). Models with refs_complement_frames=true (Kling) need both.
      if (!m?.refs_complement_frames && hasRefs && (hasFirst || hasEnd)) {
        return 'Референсы нельзя сочетать с первым или финальным кадром';
      }
      if (hasEnd && !hasFirst) {
        return 'Финальный кадр можно задать только вместе с первым';
      }
    }
    return null;
  }

  let estimateTimer: ReturnType<typeof setTimeout> | null = null;
  async function estimate() {
    if (estimateTimer) clearTimeout(estimateTimer);
    estimateTimer = setTimeout(async () => {
      estimating.value = true;
      try {
        const out = await api.post<EstimateOut>(
          '/generations/estimate',
          { ...params },
        );
        cost.value = out.cost_tokens;
        canAfford.value = out.can_afford;
      } catch (e) {
        if (e instanceof ApiCallError) {
          // Validation issues — show in UI; estimate stays the previous value.
        }
      } finally {
        estimating.value = false;
      }
    }, 250);
  }

  watch(
    () => ({
      model_key: params.model_key,
      resolution: params.resolution,
      duration: params.duration,
      generate_audio: params.generate_audio,
      type: params.type,
    }),
    () => void estimate(),
    { deep: false },
  );

  // Switching off the seedance family invalidates the active template.
  watch(
    () => params.model_key,
    (k) => {
      if (appliedTemplate.value && !isSeedanceModel(k)) {
        appliedTemplate.value = null;
      }
    },
  );

  async function submit(): Promise<{ id: string } | null> {
    submitError.value = null;
    const v = validate();
    if (v) {
      submitError.value = v;
      return null;
    }
    try {
      const out = await api.post<{ id: string; status: string; cost_tokens: number }>(
        '/generations',
        { ...params },
      );
      return { id: out.id };
    } catch (e) {
      submitError.value =
        e instanceof ApiCallError ? e.message : 'Не удалось запустить генерацию';
      return null;
    }
  }

  return {
    params,
    models,
    cost,
    canAfford,
    estimating,
    submitError,
    appliedTemplate,
    activeModel,
    reset,
    loadFromRepeat,
    applyTemplate,
    loadModels,
    validate,
    estimate,
    submit,
  };
});
